/**
 * Asana Extension - UI Prototype
 * Browse-nested Asana limb: connect, add project, task viewer, work-spec link/create.
 *
 * Access: http://flownotes.localhost/?test=asana-extension
 *
 * Bet: docs/product/bets/2026-04-16-asana-mcp-integration.md
 * Aligns with: docs/architecture/extension-design.md (Rules 4-6 surfaces + manual sync)
 *
 * Prototype constraints:
 * - Uses shared SidebarTreeRow / ws-nav chrome (Rule 4) - not bespoke row SCSS
 * - External Asana tasks open a task viewer (preview), never as FN Browse docs
 * - Create / Add project use docked or inline panels (not centered modals)
 * - Pull is explicit only (Rule 5)
 */

import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckSquare,
  ExternalLink,
  FileText,
  FolderOpen,
  Link2,
  ListTodo,
  Plus,
  RefreshCw,
  Search,
  Square,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

// --- Types ----------------------------------------------------------------

type Scene =
  | 'settings-disconnected'
  | 'settings-connected'
  | 'browse'
  | 'task-unlinked'
  | 'create-spec'
  | 'task-linked'

interface AsanaStory {
  id: string
  author: string
  at: string
  text: string
}

interface AsanaTask {
  gid: string
  name: string
  completed: boolean
  dueOn: string | null
  assignee: string | null
  section: string
  notes: string
  stories: AsanaStory[]
  linkedDoc?: { title: string; path: string } | null
}

interface AsanaProject {
  gid: string
  name: string
}

// --- Mock data ------------------------------------------------------------

const ASANA_WORKSPACE = { gid: '1200ws', name: 'Personal' }

const CATALOG_PROJECTS: AsanaProject[] = [
  { gid: '1200house', name: 'House Sale' },
  { gid: '1200chores', name: 'Household chores' },
  { gid: '1200work', name: 'Side projects' },
]

const HOUSE_SALE_TASKS: AsanaTask[] = [
  {
    gid: '1210quotes',
    name: 'Get 3 quotes',
    completed: false,
    dueOn: '2026-07-20',
    assignee: 'Alice',
    section: 'Roof',
    notes: 'Prefer metal over asphalt if budget allows.',
    stories: [
      { id: 's1', author: 'Alice', at: 'Jul 8', text: 'Sent RFQ to two roofers. Waiting on third.' },
      { id: 's2', author: 'Rowan', at: 'Jul 9', text: 'Add metal quote comparison.' },
    ],
    linkedDoc: null,
  },
  {
    gid: '1210measure',
    name: 'Measure square footage',
    completed: true,
    dueOn: '2026-07-01',
    assignee: 'Rowan',
    section: 'Roof',
    notes: 'Done - 2,140 sq ft.',
    stories: [],
    linkedDoc: null,
  },
  {
    gid: '1210plumber',
    name: 'Schedule plumber',
    completed: false,
    dueOn: '2026-07-12',
    assignee: 'Rowan',
    section: 'Water heater',
    notes: 'Need someone who can do tankless install.',
    stories: [
      { id: 's3', author: 'Rowan', at: 'Jul 10', text: 'Left voicemail with Valley Plumbing.' },
    ],
    linkedDoc: null,
  },
  {
    gid: '1210paving',
    name: 'Call paving co.',
    completed: false,
    dueOn: null,
    assignee: null,
    section: 'Driveway',
    notes: '',
    stories: [],
    linkedDoc: null,
  },
]

const MY_TASKS = HOUSE_SALE_TASKS.filter((t) => t.assignee === 'Rowan' && !t.completed)

const SCENES: { id: Scene; label: string }[] = [
  { id: 'settings-disconnected', label: 'Settings (off)' },
  { id: 'settings-connected', label: 'Settings (on)' },
  { id: 'browse', label: 'Browse' },
  { id: 'task-unlinked', label: 'Task viewer' },
  { id: 'create-spec', label: 'Create work spec' },
  { id: 'task-linked', label: 'Linked + badge' },
]

/** Browse depth -> padLeft (matches production nested browse rhythm). */
const PAD = [8, 20, 32, 44, 56] as const

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function formatDue(dueOn: string | null): string {
  if (!dueOn) return 'No due date'
  return new Date(`${dueOn}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

// --- Component ------------------------------------------------------------

export function AsanaExtensionTest() {
  const [scene, setScene] = useState<Scene>('browse')
  const [asanaExpanded, setAsanaExpanded] = useState(true)
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [houseExpanded, setHouseExpanded] = useState(true)
  const [myTasksExpanded, setMyTasksExpanded] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)
  const [addedProjects, setAddedProjects] = useState<AsanaProject[]>([
    { gid: '1200house', name: 'House Sale' },
  ])
  const [addOpen, setAddOpen] = useState(false)
  const [addQuery, setAddQuery] = useState('')
  const [selectedTaskGid, setSelectedTaskGid] = useState('1210quotes')
  const [tasks, setTasks] = useState(HOUSE_SALE_TASKS)
  const [createFolder, setCreateFolder] = useState('personal/house-sale/')
  const [pulledAt, setPulledAt] = useState<string | null>(null)
  const [harnessNote, setHarnessNote] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [validated, setValidated] = useState(false)

  const selectedTask = useMemo(
    () => tasks.find((t) => t.gid === selectedTaskGid) ?? tasks[0],
    [tasks, selectedTaskGid],
  )

  const sections = useMemo(() => {
    const map = new Map<string, AsanaTask[]>()
    for (const t of tasks) {
      if (!showCompleted && t.completed) continue
      const list = map.get(t.section) ?? []
      list.push(t)
      map.set(t.section, list)
    }
    return [...map.entries()]
  }, [tasks, showCompleted])

  const filteredCatalog = CATALOG_PROJECTS.filter((p) =>
    p.name.toLowerCase().includes(addQuery.toLowerCase()),
  )

  function note(msg: string) {
    setHarnessNote(msg)
    window.setTimeout(() => setHarnessNote(null), 2200)
  }

  function openTask(gid: string, linked = false) {
    setSelectedTaskGid(gid)
    setScene(linked ? 'task-linked' : 'task-unlinked')
  }

  function addProject(p: AsanaProject) {
    if (addedProjects.some((a) => a.gid === p.gid)) return
    setAddedProjects((prev) => [...prev, p])
    setAddOpen(false)
    setAddQuery('')
    note(`Added ${p.name} to Browse`)
  }

  function removeProject(gid: string) {
    setAddedProjects((prev) => prev.filter((p) => p.gid !== gid))
    note('Removed from Browse (Asana unchanged)')
  }

  function createWorkSpec() {
    const path = `${createFolder.replace(/\/?$/, '/')}${slugify(selectedTask.name)}.md`
    setTasks((prev) =>
      prev.map((t) =>
        t.gid === selectedTask.gid
          ? { ...t, linkedDoc: { title: selectedTask.name, path } }
          : t,
      ),
    )
    setPulledAt(new Date().toISOString())
    setScene('task-linked')
    note(`Created ${slugify(selectedTask.name)}.md`)
  }

  function pullLatest() {
    setPulledAt(new Date().toISOString())
    note('Pulled due, assignee, stories (explicit sync)')
  }

  function runValidate() {
    setValidating(true)
    window.setTimeout(() => {
      setValidating(false)
      setValidated(true)
      note('Validated: GET /users/me succeeded')
    }, 600)
  }

  const showAppChrome =
    scene === 'browse' ||
    scene === 'task-unlinked' ||
    scene === 'create-spec' ||
    scene === 'task-linked'

  return (
    <div
      className="flex h-screen flex-col bg-background text-foreground"
      data-testid={testIds.asanaExtensionTest.root}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5">
        <Button variant="ghost" size="sm" asChild>
          <a href="/?test=index">
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Index
          </a>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Asana Extension</div>
          <div className="truncate text-xs text-muted-foreground">
            Extension Design Rule 4 browse-nested · Rule 5 manual pull · mock UI
          </div>
        </div>
        <div
          className="flex flex-wrap gap-1"
          data-testid={testIds.asanaExtensionTest.sceneTabs}
        >
          {SCENES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={scene === s.id ? 'secondary' : 'ghost'}
              className="h-7 text-xs"
              onClick={() => {
                setScene(s.id)
                if (s.id === 'create-spec' || s.id === 'task-unlinked') {
                  setSelectedTaskGid('1210quotes')
                  setTasks((prev) =>
                    prev.map((t) =>
                      t.gid === '1210quotes' ? { ...t, linkedDoc: null } : t,
                    ),
                  )
                  setPulledAt(null)
                }
                if (s.id === 'task-linked') {
                  setSelectedTaskGid('1210quotes')
                  setTasks((prev) =>
                    prev.map((t) =>
                      t.gid === '1210quotes'
                        ? {
                            ...t,
                            linkedDoc: {
                              title: 'Get 3 quotes',
                              path: 'personal/house-sale/get-3-quotes.md',
                            },
                          }
                        : t,
                    ),
                  )
                  setPulledAt('2026-07-11T23:02:00.000Z')
                }
                if (s.id === 'settings-connected') setValidated(false)
              }}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </header>

      {harnessNote ? (
        <div
          className="border-b border-border bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground transition-opacity duration-300 ease-out"
          role="status"
        >
          Harness: {harnessNote}
        </div>
      ) : null}

      {/* Settings */}
      {(scene === 'settings-disconnected' || scene === 'settings-connected') && (
        <div className="flex flex-1 overflow-hidden">
          <aside className="w-52 shrink-0 border-r border-border bg-sidebar p-3 text-sidebar-foreground">
            <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Settings
            </div>
            {['General', 'Extensions', 'Secrets'].map((item) => (
              <div
                key={item}
                className={cn(
                  'px-2 py-1.5 text-sm',
                  item === 'Extensions' && 'bg-sidebar-accent font-medium',
                )}
              >
                {item}
              </div>
            ))}
          </aside>
          <main
            className="flex-1 overflow-auto px-10 py-9"
            data-testid={testIds.asanaExtensionTest.settingsPanel}
          >
            <div className="mx-auto max-w-md space-y-8">
              <div>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <h1 className="text-2xl font-semibold tracking-tight">Asana</h1>
                  <span className="text-xs text-muted-foreground">
                    {scene === 'settings-connected' ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Reach into Asana from this workspace. Tasks stay in Asana;
                  Maater links work specs and pulls on demand.
                </p>
              </div>

              {scene === 'settings-disconnected' ? (
                <div className="space-y-4 border-t border-border pt-6">
                  <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Not connected. OAuth tokens will live in user_extension_tokens.</span>
                  </div>
                  <Button
                    data-testid={testIds.asanaExtensionTest.connectButton}
                    onClick={() => {
                      setScene('settings-connected')
                      setValidated(false)
                      note('OAuth complete - validate before save')
                    }}
                  >
                    Connect Asana
                  </Button>
                </div>
              ) : (
                <div className="space-y-6 border-t border-border pt-6">
                  <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                    <Check className="h-4 w-4" />
                    Connected as rowan@example.com
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Default Asana workspace
                    </label>
                    <div className="border-b border-input py-2 text-sm">
                      {ASANA_WORKSPACE.name}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      One Asana workspace per Maater workspace (v1). Projects are
                      added from Browse.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      Credential check (Extension Design Rule 1)
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={validating}
                        onClick={runValidate}
                      >
                        {validating ? 'Validating…' : 'Validate with GET /users/me'}
                      </Button>
                      {validated ? (
                        <span className="text-xs text-emerald-700 dark:text-emerald-400">
                          Passed
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Required before save in production
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" disabled={!validated}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setScene('settings-disconnected')
                        setValidated(false)
                      }}
                    >
                      Disconnect
                    </Button>
                    <Button
                      size="sm"
                      variant="link"
                      className="px-0"
                      onClick={() => setScene('browse')}
                    >
                      Open Browse
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      )}

      {/* App: Browse + main */}
      {showAppChrome && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside
            className="flex w-[17rem] shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground"
            data-testid={testIds.asanaExtensionTest.browseSidebar}
          >
            <div className="border-b border-sidebar-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Browse
            </div>
            <div className="flex-1 overflow-auto py-1" role="tree">
              <TreeRow
                depth={0}
                label="Personal"
                icon={<FolderOpen className="h-3.5 w-3.5" />}
                expanded
              />
              <TreeRow
                depth={1}
                label="Documents"
                icon={<FileText className="h-3.5 w-3.5" />}
              />
              <TreeRow
                depth={1}
                label="Asana"
                icon={<CheckSquare className="h-3.5 w-3.5" />}
                expanded={asanaExpanded}
                onToggle={() => setAsanaExpanded((v) => !v)}
                active
                testId={testIds.asanaExtensionTest.asanaNavRow}
              />
              {asanaExpanded && (
                <>
                  <TreeRow
                    depth={2}
                    label="My Tasks"
                    icon={<ListTodo className="h-3.5 w-3.5" />}
                    expanded={myTasksExpanded}
                    onToggle={() => setMyTasksExpanded((v) => !v)}
                    badge={
                      <span className="text-[10px] text-muted-foreground">
                        {MY_TASKS.length}
                      </span>
                    }
                  />
                  {myTasksExpanded &&
                    MY_TASKS.map((t) => (
                      <TreeRow
                        key={t.gid}
                        depth={3}
                        label={t.name}
                        icon={<Square className="h-3 w-3" />}
                        onActivate={() => openTask(t.gid)}
                      />
                    ))}
                  <TreeRow
                    depth={2}
                    label="Projects"
                    icon={<FolderOpen className="h-3.5 w-3.5" />}
                    expanded={projectsExpanded}
                    onToggle={() => setProjectsExpanded((v) => !v)}
                  />
                  {projectsExpanded && (
                    <>
                      <button
                        type="button"
                        className={cn(
                          'ws-nav-item ws-nav-row w-full py-1 pr-2 text-left text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground',
                          addOpen && 'text-sidebar-foreground',
                        )}
                        style={
                          {
                            '--ws-nav-pad-left': `${PAD[3]}px`,
                          } as React.CSSProperties
                        }
                        data-testid={testIds.asanaExtensionTest.addProjectButton}
                        onClick={() => setAddOpen((v) => !v)}
                      >
                        <span className="ws-nav-row__icon">
                          <Plus className="h-3 w-3" />
                        </span>
                        <span className="ws-nav-row__label">
                          {addOpen ? 'Close search' : 'Add project'}
                        </span>
                      </button>

                      {/* Inline add panel - not a modal (frontend-design) */}
                      <div
                        className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
                        style={{ gridTemplateRows: addOpen ? '1fr' : '0fr' }}
                      >
                        <div className="overflow-hidden">
                          <div
                            className="mx-2 mb-2 mt-1 space-y-2 border border-sidebar-border bg-sidebar-accent/40 p-2"
                            data-testid={testIds.asanaExtensionTest.addProjectDialog}
                          >
                            <p className="text-[10px] text-muted-foreground">
                              In {ASANA_WORKSPACE.name}
                            </p>
                            <div className="relative">
                              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                className="h-8 border-0 bg-background/80 pl-7 text-xs shadow-none"
                                value={addQuery}
                                onChange={(e) => setAddQuery(e.target.value)}
                                placeholder="Search projects"
                                autoFocus={addOpen}
                              />
                            </div>
                            <div className="max-h-36 space-y-0.5 overflow-auto">
                              {filteredCatalog.map((p) => {
                                const added = addedProjects.some((a) => a.gid === p.gid)
                                return (
                                  <button
                                    key={p.gid}
                                    type="button"
                                    disabled={added}
                                    className={cn(
                                      'flex w-full items-center justify-between px-1.5 py-1 text-left text-xs hover:bg-sidebar-accent',
                                      added && 'cursor-not-allowed opacity-40',
                                    )}
                                    onClick={() => addProject(p)}
                                  >
                                    <span>{p.name}</span>
                                    {added ? <span>Added</span> : null}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {addedProjects.map((p) => (
                        <div key={p.gid}>
                          <TreeRow
                            depth={3}
                            label={p.name}
                            icon={<FolderOpen className="h-3.5 w-3.5" />}
                            expanded={p.gid === '1200house' ? houseExpanded : false}
                            onToggle={
                              p.gid === '1200house'
                                ? () => setHouseExpanded((v) => !v)
                                : undefined
                            }
                            onContextMenu={(e) => {
                              e.preventDefault()
                              removeProject(p.gid)
                            }}
                          />
                          {p.gid === '1200house' &&
                            houseExpanded &&
                            sections.map(([section, sectionTasks]) => (
                              <div key={section}>
                                <div
                                  className="py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                                  style={{ paddingLeft: PAD[4] }}
                                >
                                  {section}
                                </div>
                                {sectionTasks.map((t) => (
                                  <TreeRow
                                    key={t.gid}
                                    depth={4}
                                    label={t.name}
                                    icon={
                                      t.completed ? (
                                        <CheckSquare className="h-3 w-3 text-muted-foreground" />
                                      ) : (
                                        <Square className="h-3 w-3" />
                                      )
                                    }
                                    badge={
                                      t.dueOn ? (
                                        <span className="text-[10px] text-muted-foreground">
                                          {formatDue(t.dueOn)}
                                        </span>
                                      ) : undefined
                                    }
                                    onActivate={() =>
                                      openTask(t.gid, Boolean(t.linkedDoc))
                                    }
                                    active={
                                      selectedTaskGid === t.gid &&
                                      scene !== 'browse'
                                    }
                                    testId={testIds.asanaExtensionTest.taskRow(t.gid)}
                                  />
                                ))}
                              </div>
                            ))}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="border-t border-sidebar-border p-2">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
                Show completed
              </label>
            </div>
          </aside>

          <main
            className="relative flex min-w-0 flex-1 overflow-hidden"
            data-testid={testIds.asanaExtensionTest.mainPane}
          >
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {scene === 'browse' && (
                <div className="flex flex-1 flex-col justify-center px-10 py-12">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Asana in this workspace
                  </p>
                  <h2 className="mb-6 max-w-md text-2xl font-semibold tracking-tight">
                    Work stays in Asana. Context compounds here.
                  </h2>
                  <ol className="max-w-sm space-y-3 text-sm text-muted-foreground">
                    <li>
                      <span className="font-medium text-foreground">1.</span> Add
                      a project under Asana (House Sale is already here).
                    </li>
                    <li>
                      <span className="font-medium text-foreground">2.</span>{' '}
                      Click a task to preview - it does not open as a Maater
                      document until you link or create.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">3.</span>{' '}
                      Create a work spec (slug filename; GID in frontmatter) or
                      Pull after Asana changes.
                    </li>
                  </ol>
                  <Button
                    className="mt-8 w-fit"
                    variant="secondary"
                    onClick={() => openTask('1210quotes')}
                  >
                    Open “Get 3 quotes”
                  </Button>
                </div>
              )}

              {(scene === 'task-unlinked' ||
                scene === 'task-linked' ||
                scene === 'create-spec') && (
                <TaskViewer
                  task={selectedTask}
                  pulledAt={pulledAt}
                  onOpenCreate={() => setScene('create-spec')}
                  onPull={pullLatest}
                  onUnlink={() => {
                    setTasks((prev) =>
                      prev.map((t) =>
                        t.gid === selectedTask.gid
                          ? { ...t, linkedDoc: null }
                          : t,
                      ),
                    )
                    setScene('task-unlinked')
                    note('Unlinked document')
                  }}
                />
              )}
            </div>

            {/* Docked create panel - not a centered modal */}
            <div
              className="grid shrink-0 transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{
                gridTemplateColumns: scene === 'create-spec' ? '20rem' : '0fr',
              }}
            >
              <div className="min-w-0 overflow-hidden">
                <aside
                  className="flex h-full w-80 flex-col border-l border-border bg-background"
                  data-testid={testIds.asanaExtensionTest.createSpecDialog}
                >
                  <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
                    <div>
                      <h2 className="text-sm font-semibold">Create work spec</h2>
                      <p className="text-xs text-muted-foreground">
                        From Asana · slug filename
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => setScene('task-unlinked')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex-1 space-y-4 overflow-auto px-4 py-4 text-sm">
                    <Field label="Title" value={selectedTask.name} readOnly />
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        Folder
                      </label>
                      <Input
                        value={createFolder}
                        onChange={(e) => setCreateFolder(e.target.value)}
                      />
                    </div>
                    <Field
                      label="Filename"
                      value={`${slugify(selectedTask.name)}.md`}
                      hint="GID only in frontmatter"
                      readOnly
                    />
                    <pre className="overflow-x-auto whitespace-pre-wrap border-t border-border pt-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{`asana_task_gid: "${selectedTask.gid}"
asana_project_gid: "1200house"
source_url: "https://app.asana.com/0/1200house/${selectedTask.gid}"
asana_sync_mode: manual`}
                    </pre>
                  </div>
                  <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setScene('task-unlinked')}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      data-testid={testIds.asanaExtensionTest.createSpecConfirm}
                      onClick={createWorkSpec}
                    >
                      Create
                    </Button>
                  </div>
                </aside>
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  )
}

// --- Subcomponents --------------------------------------------------------

function TreeRow({
  depth,
  label,
  icon,
  expanded,
  onToggle,
  onActivate,
  onContextMenu,
  active,
  badge,
  testId,
}: {
  depth: number
  label: string
  icon: React.ReactNode
  expanded?: boolean
  onToggle?: () => void
  onActivate?: () => void
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void
  active?: boolean
  badge?: React.ReactNode
  testId?: string
}) {
  const expandable = Boolean(onToggle)
  return (
    <SidebarTreeRow
      padLeft={PAD[Math.min(depth, PAD.length - 1)]}
      active={active}
      icon={icon}
      label={label}
      badge={badge}
      data-testid={testId}
      aria-level={depth + 1}
      aria-expanded={expandable ? expanded : undefined}
      chevron={
        expandable
          ? {
              expanded: Boolean(expanded),
              onClick: (e) => {
                e.stopPropagation()
                onToggle?.()
              },
            }
          : undefined
      }
      onClick={() => {
        if (onToggle) onToggle()
        else onActivate?.()
      }}
      onContextMenu={onContextMenu}
      className="cursor-pointer"
    />
  )
}

function Field({
  label,
  value,
  hint,
  readOnly,
}: {
  label: string
  value: string
  hint?: string
  readOnly?: boolean
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input value={value} readOnly={readOnly} />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function TaskViewer({
  task,
  pulledAt,
  onOpenCreate,
  onPull,
  onUnlink,
}: {
  task: AsanaTask
  pulledAt: string | null
  onOpenCreate: () => void
  onPull: () => void
  onUnlink: () => void
}) {
  const linked = Boolean(task.linkedDoc)
  return (
    <div
      key={task.gid + (linked ? '-linked' : '')}
      className="flex h-full flex-col overflow-auto animate-in fade-in slide-in-from-bottom-1 duration-300 ease-out"
      data-testid={testIds.asanaExtensionTest.taskViewer}
    >
      {linked && (
        <div
          className="flex items-center gap-2 border-b border-border px-5 py-2 text-xs"
          data-testid={testIds.asanaExtensionTest.docBadge}
        >
          <Badge variant="secondary" className="gap-1 font-normal">
            <CheckSquare className="h-3 w-3" />
            Asana
          </Badge>
          <span className="font-medium">{task.name}</span>
          <span className="text-muted-foreground">· {formatDue(task.dueOn)}</span>
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" className="h-7" onClick={onPull}>
              <RefreshCw className="mr-1 h-3 w-3" />
              Pull
            </Button>
            <Button size="sm" variant="ghost" className="h-7" asChild>
              <a
                href={`https://app.asana.com/0/1200house/${task.gid}`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="mr-1 h-3 w-3" />
                Asana
              </a>
            </Button>
          </div>
        </div>
      )}

      <div className="border-b border-border px-8 py-6">
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            House Sale / {task.section}
          </span>
          {!linked && (
            <Button size="sm" variant="link" className="ml-auto h-auto p-0" asChild>
              <a
                href={`https://app.asana.com/0/1200house/${task.gid}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in Asana
              </a>
            </Button>
          )}
        </div>
        <h1 className="text-[1.65rem] font-semibold leading-tight tracking-tight">
          {task.name}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {task.completed ? 'Completed' : 'Incomplete'}
          <span className="mx-1.5 opacity-40">·</span>
          Due {formatDue(task.dueOn)}
          <span className="mx-1.5 opacity-40">·</span>
          {task.assignee ?? 'Unassigned'}
        </p>
      </div>

      <div className="space-y-10 px-8 py-7">
        <section>
          <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Notes
          </h2>
          <p className="max-w-prose text-sm leading-relaxed">
            {task.notes || (
              <span className="text-muted-foreground">No notes on this task</span>
            )}
          </p>
        </section>

        <section>
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Stories
            </h2>
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={onPull}
            >
              Pull latest
            </button>
          </div>
          {task.stories.length === 0 ? (
            <p className="text-sm text-muted-foreground">No comments yet</p>
          ) : (
            <ul className="max-w-prose space-y-4">
              {task.stories.map((s) => (
                <li key={s.id} className="text-sm">
                  <div className="text-xs text-muted-foreground">
                    {s.author} · {s.at}
                  </div>
                  <div className="mt-1 leading-relaxed">{s.text}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Linked work spec
          </h2>
          {linked && task.linkedDoc ? (
            <div className="max-w-md space-y-2 border-t border-border pt-3">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {task.linkedDoc.path.split('/').pop()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {task.linkedDoc.path}
                {pulledAt
                  ? ` · Last pulled ${new Date(pulledAt).toLocaleString()}`
                  : null}
              </p>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  className="text-xs font-medium underline-offset-2 hover:underline"
                  onClick={onPull}
                >
                  Pull into doc
                </button>
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  onClick={onUnlink}
                >
                  Unlink
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" onClick={onOpenCreate} data-testid={testIds.asanaExtensionTest.createSpecOpen}>
                Create work spec
              </Button>
              <Button size="sm" variant="ghost" disabled>
                <Link2 className="mr-1 h-3.5 w-3.5" />
                Link existing
              </Button>
            </div>
          )}
        </section>

        {linked && (
          <section className="max-w-md border-t border-dashed border-border pt-4">
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Frontmatter
            </h2>
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-muted-foreground">
{`asana_task_gid: "${task.gid}"
asana_project_gid: "1200house"
source_url: "https://app.asana.com/0/1200house/${task.gid}"
asana_sync_mode: manual
asana_last_pulled: "${pulledAt ?? '-'}"
due_on: "${task.dueOn ?? ''}"`}
            </pre>
          </section>
        )}
      </div>
    </div>
  )
}

export default AsanaExtensionTest
